import React, { useState, useEffect } from 'react';
import { customerService } from '../../services/customerService';
import { CustomerStatusHistoryEntry } from '../../types';
import { formatDateTime } from '../../utils/dateUtils';
import { FiActivity, FiUser, FiClock } from 'react-icons/fi';
import Icon from '../common/Icon';

interface CustomerStatusHistoryProps {
  customerId: number;
}

const SOURCE_LABELS: Record<string, string> = {
  created: 'Created',
  manual: 'Manual change',
  order: 'Order entry',
  cron: 'Automated (cron)',
};

const statusBadgeClass = (status: string): string => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'dormant':
      return 'bg-cyan-100 text-cyan-800 border-cyan-300';
    case 'lost':
      return 'bg-red-100 text-red-800 border-red-300';
    case 'inactive':
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

const CustomerStatusHistorySection: React.FC<CustomerStatusHistoryProps> = ({ customerId }) => {
  const [entries, setEntries] = useState<CustomerStatusHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadHistory();
  }, [customerId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const data = await customerService.getStatusHistory(customerId);
      setEntries(data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load status history');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
        <Icon component={FiActivity} className="w-5 h-5 text-indigo-600" />
        Status Change History
      </h3>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading status history...</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No status history recorded</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Change</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">By</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.map((entry) => (
                <tr key={entry.id || `legacy-${entry.created_at}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                    <span className="inline-flex items-center gap-1">
                      <Icon component={FiClock} className="w-3.5 h-3.5 text-gray-400" />
                      {entry.created_at ? formatDateTime(entry.created_at) : '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="text-gray-500 capitalize">{entry.old_status ?? '—'}</span>
                    <span className="mx-1 text-gray-400">→</span>
                    <span className={`px-1.5 py-0.5 rounded-full border text-xs font-semibold capitalize ${statusBadgeClass(entry.new_status)}`}>
                      {entry.new_status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {SOURCE_LABELS[entry.source] ?? entry.source}
                  </td>
                  <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                    {entry.source === 'cron' ? (
                      <span className="text-indigo-700">System / cron</span>
                    ) : entry.changed_by_name ? (
                      <span className="inline-flex items-center gap-1">
                        <Icon component={FiUser} className="w-3.5 h-3.5 text-gray-400" />
                        {entry.changed_by_name}
                      </span>
                    ) : entry.source === 'order' ? (
                      <span className="text-gray-600">Order entry</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600 max-w-md">
                    {entry.reason || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CustomerStatusHistorySection;
